import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import axiosInstance from '../config/axios.config';

// Import all output components
import EquifaxOutputComponent from '../components/reports/output/EquifaxOutputComponent';
import ExperianOutputComponent from '../components/reports/output/ExperianOutputComponent';
import BankStatementOutputComponent from '../components/reports/output/BankStatementOutputComponent';
import PANDetailsOutputComponent from '../components/reports/output/PANDetailsOutputComponent';
import EmploymentHistoryOutputComponent from '../components/reports/output/EmploymentHistoryOutputComponent';
import EHFUANOutputComponent from '../components/reports/output/EHFUANOutputComponent';
import DrivingLicenceOutputComponent from '../components/reports/output/DrivingLicenceOutputComponent';
import DrivingLicenceV2OutputComponent from '../components/reports/output/DrivingLicenceV2OutputComponent';
import VoterIdOutputComponent from '../components/reports/output/VoterIdOutputComponent';
import MobileBureauLookupOutputComponent from '../components/reports/output/MobileBureauLookupOutputComponent';
import CAMembershipOutputComponent from '../components/reports/output/CAMembershipOutputComponent';
import RCDetailsOutputComponent from '../components/reports/output/RCDetailsOutputComponent';

const SharedReportViewer = () => {
    const { shareId, reportType, reportId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);
    const [error, setError] = useState(null);
    const [shareInfo, setShareInfo] = useState(null);

    useEffect(() => {
        loadSharedReport();
    }, [shareId, reportType, reportId]);

    const loadSharedReport = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // First, get the share details to verify access
            const shareResponse = await axiosInstance.get(`/leads/shared/${shareId}/details`);
            
            if (!shareResponse.data.success) {
                setError('Unable to access this shared report');
                setLoading(false);
                return;
            }

            const shareData = shareResponse.data.data;
            setShareInfo(shareData.share);

            // Find the specific report in the shared reports
            const report = shareData.reports.find(r => 
                r.id === parseInt(reportId) && r.type === reportType
            );

            if (!report) {
                setError('Report not found in shared data');
                setLoading(false);
                return;
            }

            // Process the report data
            let processedData = report.data;
            
            // If report_json is a string, parse it
            if (processedData.report_json && typeof processedData.report_json === 'string') {
                try {
                    processedData.report_json = JSON.parse(processedData.report_json);
                } catch (e) {
                    console.error('Failed to parse report_json:', e);
                }
            }

            setReportData(processedData);
            setLoading(false);
        } catch (error) {
            console.error('Error loading shared report:', error);
            setError(error.response?.data?.message || 'Failed to load shared report');
            setLoading(false);
        }
    };

    const getOutputComponent = () => {
        if (!reportData) return null;

        // Debug logging
        console.log('SharedReportViewer - Getting output component for:', {
            reportType,
            reportName: reportData.report_name,
            reportData
        });

        // Map report types to their components - add more variations
        const componentMap = {
            'bureau_box_reports': {
                // Bureau Reports
                'EQUIFAX': EquifaxOutputComponent,
                'EQUIFAX_REPORT': EquifaxOutputComponent,
                'BUREAU_REPORT': EquifaxOutputComponent,  // This is Equifax
                'EXPERIAN': ExperianOutputComponent,
                'EXPERIAN_REPORT': ExperianOutputComponent,
                'EXPERIAN_BUREAU_REPORT': ExperianOutputComponent,
                
                // Banking
                'BANK_STATEMENT': BankStatementOutputComponent,
                'BANK_STATEMENT_ANALYSIS': BankStatementOutputComponent,
                
                // Identity Verification
                'PAN_DETAILS': PANDetailsOutputComponent,
                'PAN_VERIFICATION': PANDetailsOutputComponent,
                'DL_VERIFICATION': DrivingLicenceOutputComponent,
                'DRIVING_LICENCE': DrivingLicenceOutputComponent,
                'DRIVING_LICENCE_DETAILED': DrivingLicenceOutputComponent,
                'DRIVING_LICENCE_VERIFICATION': DrivingLicenceOutputComponent,
                'DL_DETAILS': DrivingLicenceOutputComponent,
                'DL_VERIFICATION_V2': DrivingLicenceV2OutputComponent,
                'DRIVING_LICENCE_V2': DrivingLicenceV2OutputComponent,
                'VOTER_ID': VoterIdOutputComponent,
                'VOTER_ID_VALIDATION': VoterIdOutputComponent,
                'VOTER_ID_VERIFICATION': VoterIdOutputComponent,
                'VOTER_ID_DETAILS': VoterIdOutputComponent,
                
                // Employment
                'EMPLOYMENT_HISTORY': EmploymentHistoryOutputComponent,
                'EMPLOYMENT_HISTORY_MOBILE': EmploymentHistoryOutputComponent,
                'EMPLOYMENT_HISTORY_UAN': EHFUANOutputComponent,
                'UAN_EMPLOYMENT_HISTORY': EHFUANOutputComponent,
                
                // Other Verifications
                'MOBILE_BUREAU': MobileBureauLookupOutputComponent,
                'MOBILE_BUREAU_LOOKUP': MobileBureauLookupOutputComponent,
                'CA_MEMBERSHIP': CAMembershipOutputComponent,
                'CA_MEMBERSHIP_VERIFICATION': CAMembershipOutputComponent,
                'RC_DETAILS': RCDetailsOutputComponent,
                'VEHICLE_RC': RCDetailsOutputComponent,
                
                // Additional report types found in DB
                'COURT_RECORD_CHECK': null, // No component yet
                'ACCOUNT_AGGREGATOR': null, // No component yet
                'MOBILE_TO_LPG_DETAILS': null, // No component yet
            }
        };

        // Get the appropriate component based on report type and name
        let OutputComponent = null;
        
        // Check if we have a report name
        if (reportData.report_name) {
            // Map report_name to component
            const reportNameUpper = reportData.report_name.toUpperCase().replace(/[-_\s]/g, '_');
            
            console.log('Looking for component for report name:', reportNameUpper);
            
            // Try direct mapping first
            if (reportType === 'bureau_box_reports' || reportType.includes('bureau')) {
                OutputComponent = componentMap.bureau_box_reports[reportNameUpper];
            }
            
            // If not found in direct mapping, try all tables
            if (!OutputComponent) {
                // Check all possible mappings
                for (const [table, mappings] of Object.entries(componentMap)) {
                    if (mappings[reportNameUpper]) {
                        OutputComponent = mappings[reportNameUpper];
                        console.log(`Found component in ${table} mappings`);
                        break;
                    }
                }
            }
            
            // If still not found, try pattern matching
            if (!OutputComponent) {
                console.log('Direct mapping not found, trying pattern matching...');
                const reportNameClean = reportNameUpper.replace(/_/g, ' ');
                
                if (reportNameClean.includes('EQUIFAX')) {
                    OutputComponent = EquifaxOutputComponent;
                } else if (reportNameClean.includes('EXPERIAN')) {
                    OutputComponent = ExperianOutputComponent;
                } else if (reportNameClean.includes('BANK') && reportNameClean.includes('STATEMENT')) {
                    OutputComponent = BankStatementOutputComponent;
                } else if (reportNameClean.includes('PAN')) {
                    OutputComponent = PANDetailsOutputComponent;
                } else if (reportNameClean.includes('EMPLOYMENT') && reportNameClean.includes('UAN')) {
                    OutputComponent = EHFUANOutputComponent;
                } else if (reportNameClean.includes('EMPLOYMENT')) {
                    OutputComponent = EmploymentHistoryOutputComponent;
                } else if (reportNameClean.includes('DL') || reportNameClean.includes('DRIVING') || reportNameClean.includes('LICENCE')) {
                    OutputComponent = reportNameClean.includes('V2') ? DrivingLicenceV2OutputComponent : DrivingLicenceOutputComponent;
                } else if (reportNameClean.includes('VOTER')) {
                    OutputComponent = VoterIdOutputComponent;
                } else if (reportNameClean.includes('MOBILE') && reportNameClean.includes('BUREAU')) {
                    OutputComponent = MobileBureauLookupOutputComponent;
                } else if (reportNameClean.includes('CA') && reportNameClean.includes('MEMBER')) {
                    OutputComponent = CAMembershipOutputComponent;
                } else if (reportNameClean.includes('RC') || reportNameClean.includes('VEHICLE')) {
                    OutputComponent = RCDetailsOutputComponent;
                }
            }
            
            console.log('Selected component:', OutputComponent ? OutputComponent.name : 'None');
        } else {
            console.log('No report_name found in reportData');
        }

        if (!OutputComponent) {
            // Fallback to displaying raw data
            return (
                <div className="p-4">
                    <Message severity="info" text={`No specific viewer available for report type: ${reportData.report_name}`} />
                    <div className="mt-3 p-3 surface-100 border-round">
                        <h5>Report Data:</h5>
                        <pre className="overflow-auto" style={{ maxHeight: '600px' }}>
                            {JSON.stringify(reportData.report_json || reportData, null, 2)}
                        </pre>
                    </div>
                </div>
            );
        }

        // Pass the report data to the output component
        // Most output components expect the data in a specific format
        // Parse report_json if it's a string
        let parsedReportJson = reportData.report_json;
        if (typeof parsedReportJson === 'string') {
            try {
                parsedReportJson = JSON.parse(parsedReportJson);
            } catch (e) {
                console.error('Failed to parse report_json:', e);
                parsedReportJson = reportData.report_json;
            }
        }
        
        const componentProps = {
            reportData: parsedReportJson || reportData,
            reportId: reportId,
            leadId: reportData.lead_id,
            // Some components might expect the full report object
            fullReportData: reportData,
            // Add onBack handler for navigation - go back to SharedLeadDetails
            onBack: () => navigate(`/app/leads/shared/${shareId}`)
        };

        console.log('Rendering component with props:', componentProps);

        return <OutputComponent {...componentProps} />;
    };

    if (loading) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <ProgressSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4">
                <Message severity="error" text={error} />
                <Button 
                    label="Back to Lead Details" 
                    icon="pi pi-arrow-left"
                    className="mt-3"
                    onClick={() => navigate(`/app/leads/shared/${shareId}`)}
                />
            </div>
        );
    }

    // Format report name for display
    const formatReportName = (reportName) => {
        if (!reportName) return 'Report';
        
        const nameMap = {
            'BANK_STATEMENT': 'Bank Statement Analysis',
            'BUREAU_REPORT': 'Equifax Credit Report',
            'PAN_VERIFICATION': 'PAN Verification',
            'PAN_DETAILS': 'PAN Details',
            'EMPLOYMENT_HISTORY': 'Employment History (Mobile)',
            'UAN_EMPLOYMENT_HISTORY': 'UAN Employment History',
            'DRIVING_LICENCE_DETAILED': 'Driving Licence (Detailed)',
            'DRIVING_LICENCE_VERIFICATION': 'Driving Licence Verification',
            'DL_DETAILS': 'DL Details',
            'MOBILE_BUREAU_LOOKUP': 'Mobile Bureau Lookup',
            'VOTER_ID_VERIFICATION': 'Voter ID Verification',
            'VOTER_ID_DETAILS': 'Voter ID Details',
            'CA_MEMBERSHIP_VERIFICATION': 'CA Membership Verification',
            'COURT_RECORD_CHECK': 'Court Record Check',
            'ACCOUNT_AGGREGATOR': 'Account Aggregator',
            'RC_DETAILS': 'RC Details',
            'EXPERIAN_BUREAU_REPORT': 'Experian Credit Report',
            'MOBILE_TO_LPG_DETAILS': 'Mobile to LPG Details'
        };
        
        return nameMap[reportName] || reportName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
        <div className="shared-report-viewer">
            {shareInfo && (
                <div className="surface-card border-round shadow-2 p-3 mb-3">
                    <div className="flex align-items-center">
                        <Button 
                            icon="pi pi-arrow-left"
                            className="p-button-rounded p-button-text p-button-plain mr-3"
                            onClick={() => navigate(`/app/leads/shared/${shareId}`)}
                            tooltip="Back to Lead Details"
                            tooltipOptions={{ position: 'right' }}
                        />
                        <div className="flex-1">
                            <h5 className="m-0">Shared Report View</h5>
                            <p className="text-500 text-xs m-0 mt-1">
                                Shared by: {shareInfo.shared_by?.name || shareInfo.sharedBy?.name} • 
                                Access: {shareInfo.share_type === 'view' ? 'View Only' : 
                                        shareInfo.share_type === 'edit' ? 'Can Edit' : 'Full Access'} • 
                                Report: {formatReportName(reportData?.report_name)}
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            {getOutputComponent()}
        </div>
    );
};

export default SharedReportViewer;